import { addWeeks, format } from 'date-fns';
import { useCallback } from 'react';

import { usePushChanges } from '@/contexts/PushChangesContext';
import { enhancedApi as externalTaskApi } from '@/services/api/generated/externalTaskService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { CanonicalTaskType } from '@/services/types/task-types';

interface UseTreatmentCheckInPushOptions {
  matterId: string;
}

interface PushCheckInChangesParams {
  /** General notes from the check-in to add as a comment */
  generalNotes?: string | null;
  /** Number of weeks to push the due date forward (default: 2) */
  weeksToExtend?: number;
}

/**
 * Hook that provides push changes functionality for treatment check-ins.
 *
 * When a treatment check-in is finalized, this hook allows pushing:
 * - General notes as a comment to linked external tasks
 * - Due date extended by a specified number of weeks (default: 2)
 *
 * @example
 * ```tsx
 * const { pushCheckInChanges, isReady } = useTreatmentCheckInPush({ matterId });
 *
 * const onSubmit = async (values) => {
 *   await finalizeCheckIn(...);
 *   await pushCheckInChanges({ generalNotes: values.general_notes });
 * };
 * ```
 */
export const useTreatmentCheckInPush = ({
  matterId,
}: UseTreatmentCheckInPushOptions) => {
  const { pushChanges } = usePushChanges();

  // Query for the Treatment Check-In task to get its ID for push changes
  const { data: treatmentCheckInTasksData } = useGetTasksQuery({
    matter: [matterId],
    canonical_types: [CanonicalTaskType.TREATMENT_CHECK_IN],
    limit: 1,
  });
  const treatmentCheckInTask = treatmentCheckInTasksData?.results?.[0];

  // Query linked external tasks for the treatment check-in task
  const { data: linkedExternalTasks = [] } =
    externalTaskApi.useExternalTaskByInternalTaskQuery(
      { internalTaskId: treatmentCheckInTask?.id ?? '' },
      { skip: !treatmentCheckInTask?.id },
    );

  const pushCheckInChanges = useCallback(
    async ({
      generalNotes,
      weeksToExtend = 2,
    }: PushCheckInChangesParams = {}) => {
      if (!treatmentCheckInTask) {
        return;
      }

      const changesToPush: {
        changeType: 'comment' | 'due_date';
        changeData: { comment?: string; due_date?: string };
      }[] = [];

      // Add general notes as a comment
      if (generalNotes?.trim()) {
        changesToPush.push({
          changeType: 'comment',
          changeData: { comment: generalNotes },
        });
      }

      // Push due date out by specified weeks
      const newDueDate = addWeeks(new Date(), weeksToExtend);
      changesToPush.push({
        changeType: 'due_date',
        changeData: { due_date: format(newDueDate, 'yyyy-MM-dd') },
      });

      if (changesToPush.length > 0) {
        await pushChanges({
          internalTaskId: treatmentCheckInTask.id,
          changes: changesToPush,
        });
      }
    },
    [treatmentCheckInTask, pushChanges],
  );

  return {
    /** Push check-in changes (notes as comment, due date extension) to external tasks */
    pushCheckInChanges,
    /** Whether the hook is ready to push changes (task data loaded) */
    isReady: !!treatmentCheckInTask,
    /** The treatment check-in task if found */
    treatmentCheckInTask,
    /** Whether the treatment check-in task has linked external tasks */
    hasLinkedExternalTasks: linkedExternalTasks.length > 0,
  };
};
